import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

import type { ActionAlert } from "@/api/action_alerts";
import type { PermitApplicationFocusRequest } from "@/components/permit_suite/permitSuiteNavigation";
import { sectionForPermitTab, type PermitSuiteTab } from "@/components/permit_suite/permitSuiteTabs";
import { useUser } from "@/context/UserProvider";
import { canAccessPermitTab, nearestAccessiblePermitTab } from "@/lib/permitSuiteAccess";
import type { ActiveSection } from "@/types/appNavigation";

type PermitSuiteNavigationContextValue = {
  focusRequest: PermitApplicationFocusRequest | null;
  clearPermitApplicationFocus: () => void;
  navigateToPermitApplication: (applicationId: number) => void;
  handleActionAlertSelect: (alert: ActionAlert) => void;
  navigateToPermitTab: (tab: PermitSuiteTab) => void;
};

const PermitSuiteNavigationContext = createContext<PermitSuiteNavigationContextValue | null>(null);

type PermitSuiteNavigationProviderProps = {
  children: ReactNode;
  setActiveSection: React.Dispatch<React.SetStateAction<ActiveSection>>;
};

export function PermitSuiteNavigationProvider({
  children,
  setActiveSection,
}: PermitSuiteNavigationProviderProps) {
  const { currentUser } = useUser();
  const [focusRequest, setFocusRequest] = useState<PermitApplicationFocusRequest | null>(null);

  const clearPermitApplicationFocus = useCallback(() => {
    setFocusRequest(null);
  }, []);

  const navigateToPermitApplication = useCallback(
    (applicationId: number) => {
      // Permit applications live on the pipeline tab. Today this is only
      // reachable via alerts PermitSuite::ActionAlertsProvider already gates
      // on permit_suite server-side; this check is defense in depth so a
      // future alert source can't reopen the blank-pane failure mode below
      // by depending on a server-side gate staying correct forever.
      if (!canAccessPermitTab(currentUser, "pipeline")) return;
      setActiveSection("permit_suite");
      setFocusRequest((current) => ({
        applicationId,
        nonce: (current?.nonce ?? 0) + 1,
      }));
    },
    [setActiveSection, currentUser],
  );

  const navigateToPermitTab = useCallback(
    (tab: PermitSuiteTab) => {
      // A caller (e.g. a site_plan action alert, which today is not itself
      // gated on plan_review_access) can request a tab the user's flags
      // deny. Land on a tab they actually have instead of a blank pane; if
      // they have none of the three permitting flags, do nothing.
      const accessibleTab = canAccessPermitTab(currentUser, tab) ? tab : nearestAccessiblePermitTab(currentUser);
      if (!accessibleTab) return;
      setActiveSection(sectionForPermitTab(accessibleTab));
    },
    [setActiveSection, currentUser],
  );

  const handleActionAlertSelect = useCallback(
    (alert: ActionAlert) => {
      const target = alert.target;
      if (alert.source === "permit_suite" && target?.type === "permit_application") {
        navigateToPermitApplication(target.id);
        return;
      }
      if (alert.source === "permit_suite" && alert.application_id) {
        navigateToPermitApplication(alert.application_id);
      }
    },
    [navigateToPermitApplication],
  );

  const value = useMemo(
    () => ({
      focusRequest,
      clearPermitApplicationFocus,
      navigateToPermitApplication,
      handleActionAlertSelect,
      navigateToPermitTab,
    }),
    [
      focusRequest,
      clearPermitApplicationFocus,
      navigateToPermitApplication,
      handleActionAlertSelect,
      navigateToPermitTab,
    ],
  );

  return (
    <PermitSuiteNavigationContext.Provider value={value}>
      {children}
    </PermitSuiteNavigationContext.Provider>
  );
}

export function usePermitSuiteNavigation() {
  const context = useContext(PermitSuiteNavigationContext);
  if (!context) {
    throw new Error("usePermitSuiteNavigation must be used within PermitSuiteNavigationProvider");
  }
  return context;
}
